import { useCallback, useMemo, useRef, useState } from 'react';
import { ButtonVariants, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { ConfirmDialog } from '@/components/feedback';
import { buildTableLabels } from '@/config/tableLabels';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  useDeleteCustomerTransportationMutation,
  useGetCustomerTransportationQuery,
} from '../api/customersApi';
import { buildCustomerTransportationColumns } from '../constants/customerTransportationColumns';

const getRouteRowId = (row) => row.id;

/**
 * Props:
 *   customer            the customer detail view model — its id scopes the table
 *   onAddTransportation  opens the add modal the page owns
 *   onEditTransportation (route) => void — opens the same modal on that route
 */
export const CustomerTransportationTabView = ({
  customer,
  onAddTransportation,
  onEditTransportation,
}) => {
  const t = useTranslations();
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [search, setSearch] = useState('');

  const { data, isFetching } = useGetCustomerTransportationQuery(customer.id);
  const [deleteRoute] = useDeleteCustomerTransportationMutation();
  const allRows = data?.rows ?? EMPTY_LIST;

  // One unpaginated read for the whole customer, so search filters it client-side.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allRows;
    return allRows.filter((row) =>
      [row.routeName, row.shippingFrom, row.sentTo].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [allRows, search]);

  // Ids with a DELETE in flight — gates the double-click RTK Query won't dedupe.
  const deletingIdsRef = useRef(new Set());

  const handleDelete = useCallback((row) => setRouteToDelete(row), []);
  const handleCancelDelete = useCallback(() => setRouteToDelete(null), []);

  const handleConfirmDelete = useCallback(async () => {
    const id = routeToDelete?.id;
    if (id == null || deletingIdsRef.current.has(id)) return;
    deletingIdsRef.current.add(id);
    try {
      await deleteRoute({ customerId: customer.id, id }).unwrap();
      setRouteToDelete(null);
    } catch {
      // notifyApiErrors already surfaced it.
    } finally {
      deletingIdsRef.current.delete(id);
    }
  }, [deleteRoute, customer.id, routeToDelete]);

  const columns = useMemo(
    () =>
      buildCustomerTransportationColumns(t, {
        onEdit: onEditTransportation,
        onDelete: handleDelete,
      }),
    [t, onEditTransportation, handleDelete],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'add-transportation',
        label: t('CUSTOMERS_ACTION_ADD_TRANSPORTATION'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: onAddTransportation,
      },
    ],
    [t, onAddTransportation],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('CUSTOMER_TRANSPORT_EMPTY_TITLE'),
      description: t('CUSTOMER_TRANSPORT_EMPTY_DESCRIPTION'),
      primaryLabel: t('CUSTOMERS_ACTION_ADD_TRANSPORTATION'),
      onPrimaryClick: onAddTransportation,
    }),
    [t, onAddTransportation],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        loading={isFetching}
        getRowId={getRouteRowId}
        searchable
        onSearchChange={setSearch}
        toolbarActions={toolbarActions}
        emptyState={emptyState}
        labels={tableLabels}
      />

      <ConfirmDialog
        open={routeToDelete !== null}
        title={t('CUSTOMER_TRANSPORT_DELETE_TITLE')}
        bodyTitle={t('CUSTOMER_TRANSPORT_DELETE_BODY_TITLE', {
          routeName: routeToDelete?.routeName,
        })}
        description={t('CUSTOMER_TRANSPORT_DELETE_DESCRIPTION', {
          customerSiteName: routeToDelete?.sentTo,
          siteName: routeToDelete?.shippingFrom,
        })}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};
